<script lang="ts" setup>
import { from } from '@nuts/auto-delivery-sdk/dist/utils/money';
import { useVModel } from '@vueuse/core';
import { computed, ref, watch } from 'vue';
import { useStore } from 'vuex';

import CheckCircleIcon from '@/components/base/assets/CheckCircleIcon.vue';
import Checkbox from '@/components/base/form/Checkbox.vue';
import FloatingLabel from '@/components/base/form/FloatingLabel.vue';
import InlineError from '@/components/base/form/InlineError.vue';
import Input from '@/components/base/form/Input.vue';
import Select from '@/components/base/form/OutlinedSelect.vue';
import InformationBanner from '@/components/base/layout/InformationBanner.vue';
import ThemedButton from '@/components/base/ThemedButton.vue';
import BaseBodyText from '@/components/base/typography/BaseBodyText.vue';
import SmallBodyText from '@/components/base/typography/SmallBodyText.vue';
import UnstyledButton from '@/components/base/UnstyledButton.vue';
import { useCallback } from '@/composables/useCallback';
import { useDelivery } from '@/composables/useDelivery';
import { useLocalStorage } from '@/composables/useLocalStorage';
import { usePayment } from '@/composables/usePayment';
import money from '@/filters/money';
import { NutsAddress } from '@/utils/address';
import { Money } from '@/utils/money';
import { reportError } from '@/utils/reportError';

const props = defineProps<{ modelValue: boolean }>();
const emit = defineEmits<{ 'update:modelValue': [boolean] }>();

const store = useStore();
const { hasPaymentAdjustment, paymentItems, removePaymentAdjustment, setPaymentAdjustment } =
  usePayment(store, ref(false));
const { hasMultipleShipments, setShippingChargeAdjustment, standardShipments } = useDelivery(store);

// TODO: This throws during SSR; Payment typically redirects but soon will always be loaded server-side for 1-Click Checkout
const localStorage = useLocalStorage('checkout');

const priceAdjustment = ref({ amount: '', reason: '' });
const setPriceAdjustment = useVModel(props, 'modelValue', emit);
const selectedAdjustmentType = ref<string>();

const options = computed(() => {
  const paymentAdjustment = { label: 'Payment Adjustment', value: 'paymentAdjustment' };
  const shippingCharge = { label: 'Shipping charge adjustment', value: 'shippingCharge' };
  if (hasMultipleShipments.value) {
    return [paymentAdjustment];
  }
  return [shippingCharge, paymentAdjustment];
});

const amountLabel = computed(() =>
  selectedAdjustmentType.value === 'shippingCharge' ? 'Total Shipping Charge' : 'Adjustment Amount',
);

const currentShippingCharge = computed(() =>
  paymentItems.value.find((item) => item.label === 'Shipping'),
);

const currentAdjustment = computed(() =>
  paymentItems.value.find((item) => item.label === 'Adjustment'),
);

const isLoading = ref(false);
const errorMessage = ref('');
const showError = ref(false);
const showAmountError = ref(false);
const showReasonError = ref(false);

const hasShippingAdjustment = computed(
  () =>
    !hasMultipleShipments.value &&
    localStorage.get<Money>('originalShippingValue') &&
    currentShippingCharge.value?.amount &&
    localStorage.get<Money>('originalShippingValue')?.centAmount !==
      currentShippingCharge.value.amount.centAmount,
);

const onApply = async () => {
  if (!priceAdjustment.value.amount) {
    showAmountError.value = true;
  }

  const isPaymentAdjustment = selectedAdjustmentType.value === 'paymentAdjustment';
  if (isPaymentAdjustment && !priceAdjustment.value.reason) {
    showReasonError.value = true;
  }

  if (priceAdjustment.value.amount) {
    try {
      isLoading.value = true;
      const moneyValue = from(Number(priceAdjustment.value.amount));
      if (selectedAdjustmentType.value === 'shippingCharge') {
        if (Number(priceAdjustment.value.amount) < 0) {
          showAmountError.value = true;
          return;
        }
        const [shipment] = standardShipments.value;
        if (!shipment) return;
        await setShippingChargeAdjustment(moneyValue, NutsAddress.toCt(shipment.address));
        if (!localStorage.get<Money>('originalShippingValue') && shipment.customFields?.price) {
          localStorage.set('originalShippingValue', shipment.customFields.price);
        }
      } else if (isPaymentAdjustment && priceAdjustment.value.reason) {
        await setPaymentAdjustment(moneyValue, priceAdjustment.value.reason);
      }
    } catch (e: any) {
      errorMessage.value = reportError(e.body?.message ?? e);
      showError.value = true;
    } finally {
      if (!showError.value && !showReasonError.value && !showAmountError.value) {
        priceAdjustment.value = { amount: '', reason: '' };
      }
      isLoading.value = false;
    }
  }
};

const onAmountInputChange = () => {
  showAmountError.value = false;
  showError.value = false;
};

const onReasonInputChange = () => {
  showReasonError.value = false;
  showError.value = false;
};

const removeShippingChargeAdjustment = useCallback(async () => {
  const valueToRestore = localStorage.get<Money>('originalShippingValue');
  if (!standardShipments.value.length || !valueToRestore) return;
  const [shipment] = standardShipments.value;
  await setShippingChargeAdjustment(valueToRestore, NutsAddress.toCt(shipment.address));
});

const removePaymentAdjustmentCb = useCallback(async () => {
  await removePaymentAdjustment();
});

watch(selectedAdjustmentType, () => {
  priceAdjustment.value.amount = '';
  priceAdjustment.value.reason = '';
  showAmountError.value = false;
  showReasonError.value = false;
  showError.value = false;
});

watch(hasMultipleShipments, () => {
  if (hasMultipleShipments.value && selectedAdjustmentType.value === 'shippingCharge') {
    selectedAdjustmentType.value = undefined;
  }
});
</script>

<template>
  <div
    class="flex flex-col"
    :class="{ 'gap-3': setPriceAdjustment || hasShippingAdjustment || hasPaymentAdjustment }"
  >
    <Checkbox v-model="setPriceAdjustment" data-test="price-adjustment-selector">
      <div
        class="w-full mb-0 ml-1 leading-6 cursor-pointer md:leading-7"
        :class="setPriceAdjustment ? 'font-semibold' : 'font-normal'"
      >
        Adjustments
      </div>
    </Checkbox>
    <InformationBanner v-if="showError" variant="error">
      <BaseBodyText class="text-nuts-red-900" data-test="price-adjustment-api-error">
        {{ errorMessage }}
      </BaseBodyText>
    </InformationBanner>
    <div v-if="hasShippingAdjustment">
      <div class="flex justify-between">
        <div class="flex items-center gap-2">
          <CheckCircleIcon class="text-nuts-lime-900" :size="20" />
          <SmallBodyText>Shipping adjustment applied</SmallBodyText>
          <UnstyledButton
            class="inline-flex w-fit"
            :class="{
              'animate-pulse pointer-events-none': removeShippingChargeAdjustment.isPending,
            }"
            @click="removeShippingChargeAdjustment.execute"
          >
            <SmallBodyText class="underline text-nuts-cyan-600"> Remove </SmallBodyText>
          </UnstyledButton>
        </div>
        <SmallBodyText>
          {{ money(currentShippingCharge?.amount ?? 0) }}
        </SmallBodyText>
      </div>
    </div>
    <div v-if="currentAdjustment?.amount">
      <div class="flex justify-between">
        <div class="flex items-center gap-2">
          <CheckCircleIcon class="text-nuts-lime-900" :size="20" />
          <SmallBodyText>Payment adjustment applied</SmallBodyText>
          <UnstyledButton
            class="inline-flex w-fit"
            :class="{
              'animate-pulse pointer-events-none': removePaymentAdjustmentCb.isPending,
            }"
            @click="removePaymentAdjustmentCb.execute"
          >
            <SmallBodyText class="underline text-nuts-cyan-600"> Remove </SmallBodyText>
          </UnstyledButton>
        </div>
        <SmallBodyText> {{ money(currentAdjustment?.amount) }}</SmallBodyText>
      </div>
    </div>
    <Select
      v-show="setPriceAdjustment"
      v-model="selectedAdjustmentType"
      aria-label="select order tag"
      class="h-auto"
      data-test="select-order-tag"
      name="order-tags"
      :options="options"
      placeholder="Select a reason"
      showPlaceholder
    />
    <div v-show="setPriceAdjustment && selectedAdjustmentType" class="flex flex-col gap-5">
      <div class="relative w-full">
        <Input
          v-model="priceAdjustment.amount"
          :aria-label="amountLabel"
          class="w-full placeholder-neutral-500"
          data-test="price-adjustment-amount"
          id="priceAdjustment"
          :invalid="showAmountError"
          name="priceAdjustment"
          placeholder="$0.00"
          required
          type="number"
          @input="onAmountInputChange"
        />
        <FloatingLabel class="left-0" for="priceAdjustment" data-test="recipient-name-label">
          {{ amountLabel }}
        </FloatingLabel>
        <InlineError
          v-if="showAmountError"
          :errors="['Please enter a valid amount']"
          data-test="price-adjustment-amount-validation-error"
          :fieldName="amountLabel"
        />
      </div>
      <div v-if="selectedAdjustmentType === 'paymentAdjustment'" class="relative w-full">
        <textarea
          v-model="priceAdjustment.reason"
          class="w-full h-24 p-4 text-base border border-solid font-proxima-nova focus:border-black focus:outline-none"
          :class="showReasonError ? 'border-nuts-red-800' : 'border-neutral-300'"
          id="priceAdjustmentReason"
          placeholder="Please enter the reason of adjustment"
          aria-label="Reason"
          maxlength="500"
          data-test="price-adjustment-reason"
          @input="onReasonInputChange"
        />
        <InlineError
          v-if="showReasonError"
          :errors="['Please enter a reason']"
          data-test="price-adjustment-reason-validation-error"
          fieldName="Price Adjustement Reason"
        />
      </div>
      <SmallBodyText v-else :class="{ 'mt-2': showAmountError }">
        Enter the total shipping charge above to override the current charge
      </SmallBodyText>
      <div class="flex justify-end gap-4">
        <ThemedButton
          theme="gray"
          data-test="price-adjustment-submit"
          :disabled="!selectedAdjustmentType"
          :isLoading="isLoading"
          @click="onApply"
        >
          Apply
        </ThemedButton>
      </div>
    </div>
  </div>
</template>
